import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FolderLock, Plus, Trash2, Upload, FileText, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DealRoomsTab() {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [docs, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', access_emails: '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchRooms = useCallback(async () => { try { const r = await axios.get(`${API}/deal-rooms`, { withCredentials: true }); setRooms(r.data); } catch {} }, []);
  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const fetchDocs = useCallback(async (roomId) => { try { const r = await axios.get(`${API}/deal-rooms/${roomId}/documents`, { withCredentials: true }); setDocs(r.data); } catch {} }, []);
  useEffect(() => { if (selectedRoom) fetchDocs(selectedRoom); }, [selectedRoom, fetchDocs]);

  const createRoom = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      const payload = { name: form.name, description: form.description, access_emails: form.access_emails.split(',').map(e => e.trim()).filter(Boolean) };
      await axios.post(`${API}/deal-rooms`, payload, { withCredentials: true }); toast.success('Deal room created'); setShowForm(false); setForm({ name: '', description: '', access_emails: '' }); fetchRooms();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const deleteRoom = async (id) => { try { await axios.delete(`${API}/deal-rooms/${id}`, { withCredentials: true }); toast.success('Deleted'); if (selectedRoom === id) { setSelectedRoom(null); setDocs([]); } fetchRooms(); } catch { toast.error('Failed'); } };
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file || !selectedRoom) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { await axios.post(`${API}/deal-rooms/${selectedRoom}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Uploaded'); fetchDocs(selectedRoom); } catch { toast.error('Upload failed'); }
    setUploading(false); if (fileRef.current) fileRef.current.value = '';
  };
  const copyAccessCode = (code) => { navigator.clipboard.writeText(code); toast.success('Access code copied!'); };

  return (
    <div data-testid="deal-rooms-tab">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Deal Rooms</h1><p className="text-sm text-slate-400">Secure document sharing for active transactions.</p></div>
        {!showForm && <Button onClick={() => setShowForm(true)} className="bg-[#002FA7] text-white text-xs gap-1.5 rounded-lg" data-testid="new-room-btn"><Plus size={14} /> New Room</Button>}
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="room-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div><Label className="text-xs text-slate-500">Room Name *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="123 Main St - Due Diligence" data-testid="room-name" /></div>
            <div><Label className="text-xs text-slate-500">Access Emails (comma-sep)</Label><Input value={form.access_emails} onChange={e => setForm(f => ({...f, access_emails: e.target.value}))} placeholder="buyer@example.com, attorney@firm.com" /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Description</Label><Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} /></div>
          </div>
          <div className="flex gap-2"><Button onClick={createRoom} className="bg-[#002FA7] text-white text-xs rounded-lg" data-testid="create-room-btn">Create Room</Button><Button variant="outline" onClick={() => setShowForm(false)} className="text-xs rounded-lg">Cancel</Button></div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Room list */}
        <div className="space-y-2">
          {rooms.length === 0 && !showForm ? (
            <div className="bg-white border border-slate-200 p-8 rounded-xl text-center"><FolderLock size={32} className="text-slate-200 mx-auto mb-3" /><p className="text-sm text-slate-400">No deal rooms yet.</p></div>
          ) : rooms.map(r => (
            <button key={r.id} onClick={() => setSelectedRoom(r.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedRoom === r.id ? 'border-[#002FA7] bg-[#EFF3FF]/50' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-testid={`room-${r.id}`}>
              <div className="text-sm font-semibold text-slate-800">{r.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">{r.document_count || 0} documents</div>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={(e) => { e.stopPropagation(); copyAccessCode(r.access_code); }} className="text-[9px] text-[#002FA7] hover:underline flex items-center gap-0.5"><Copy size={8} /> Code</button>
                <button onClick={(e) => { e.stopPropagation(); deleteRoom(r.id); }} className="text-[9px] text-red-400 hover:underline flex items-center gap-0.5"><Trash2 size={8} /> Delete</button>
              </div>
            </button>
          ))}
        </div>

        {/* Documents */}
        <div className="md:col-span-2">
          {selectedRoom ? (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800">Documents</h3>
                <div><input type="file" ref={fileRef} onChange={uploadDoc} className="hidden" /><Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#002FA7] text-white text-xs gap-1 rounded-lg h-8" data-testid="upload-room-doc"><Upload size={12} /> {uploading ? 'Uploading...' : 'Upload'}</Button></div>
              </div>
              {docs.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No documents yet. Upload files to share with counterparties.</p>
              ) : (
                <div className="space-y-2">
                  {docs.map(d => (
                    <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100">
                      <FileText size={16} className="text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{d.filename}</div><div className="text-[10px] text-slate-400">{d.created_at?.slice(0, 10)} &middot; {d.content_type}</div></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center"><p className="text-sm text-slate-400">Select a deal room to view documents.</p></div>
          )}
        </div>
      </div>
    </div>
  );
}
